import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../../api";
import { money, minor, ownerAccount, type AccountingSnapshot, type Owner } from "../../money/types";
import { CommandForm, MoneyInput } from "../commerce/shared";
import { Input } from "../ui/input";
import { PartyChoice } from "../ui/party-choice";
import { Field } from "../ui/workflow";
import { initialMoneyValues, moneyPayload, type MoneyValues } from "./model";

const owners = [{ value: "kolya", label: "Коля" }, { value: "vlad", label: "Влад" }, { value: "both", label: "Оба" }];

export function ExpenseForm({ snapshot, owner = "kolya", onClose }: { snapshot: AccountingSnapshot; owner?: Owner; onClose: () => void }) {
  const [current, setCurrent] = useState(snapshot);
  const fieldsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let timer = 0;
    const adjust = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const active = document.activeElement, fields = fieldsRef.current;
        const body = fields?.closest<HTMLElement>(".workflow-body");
        if (!(active instanceof HTMLInputElement) || !fields?.contains(active) || !body) return;
        const bounds = body.getBoundingClientRect(), input = active.getBoundingClientRect();
        const total = active.closest('[data-expense-key="vlad"]') ? fields.querySelector<HTMLElement>("[data-expense-total]")?.getBoundingClientRect() : undefined;
        // Keep the shared total alongside Vlad's amount whenever both fit.
        // On very short viewports the active input takes priority.
        const bottom = total && total.bottom - input.top + 24 <= body.clientHeight ? total.bottom + 12 : input.bottom + 12;
        if (bottom > bounds.bottom) body.scrollTop += bottom - bounds.bottom;
        else if (input.top - 12 < bounds.top) body.scrollTop += input.top - 12 - bounds.top;
      }, 80);
    };
    document.addEventListener("focusin", adjust);
    window.visualViewport?.addEventListener("resize", adjust);
    window.visualViewport?.addEventListener("scroll", adjust);
    window.addEventListener("resize", adjust);
    return () => {
      window.clearTimeout(timer); document.removeEventListener("focusin", adjust);
      window.visualViewport?.removeEventListener("resize", adjust);
      window.visualViewport?.removeEventListener("scroll", adjust);
      window.removeEventListener("resize", adjust);
    };
  }, []);
  const cache = useQueryClient();
  useEffect(() => { setCurrent(snapshot); }, [snapshot]);
  const [v, set] = useState<MoneyValues>({ ...initialMoneyValues, owner });
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const touch = (key: string) => setTouched(previous => ({ ...previous, [key]: true }));
  const update = (key: "amount" | "description" | "kolya" | "vlad", value: string) => set(s => ({ ...s, [key]: value }));
  const available = (who: Owner) => {
    const account = current.accounts.find(row => row.id === ownerAccount(who) && row.currency === "RUB");
    const balance = account?.owner_balances[who];
    return balance == null ? undefined : Math.min((account as typeof account & { available_minor?: number })?.available_minor ?? balance, balance);
  };
  let valid = false, total: number | undefined;
  try { moneyPayload("expense", v, current); valid = true; }
  catch { /* Incomplete fields keep submission disabled. */ }
  try {
    const sum = minor(v.kolya || "0") + minor(v.vlad || "0");
    if ((v.kolya || v.vlad) && Number.isSafeInteger(sum)) total = sum;
  } catch { /* Do not display an invalid total. */ }
  const amountError = (key: "amount" | "kolya" | "vlad", who: Owner) => {
    if (!touched[key]) return "";
    let value: number;
    try { value = minor(v[key]); } catch { return "Целое число больше 0"; }
    if (value <= 0) return "Целое число больше 0";
    const balance = available(who);
    return balance != null && value > balance ? "Больше доступного" : "";
  };
  const amountField = (key: "amount" | "kolya" | "vlad", who: Owner, label: string) => {
    const error = amountError(key, who);
    return <div key={key} data-expense-key={key} className="space-y-1.5">
      <MoneyInput label={label} value={v[key]} onChange={value => update(key, value)} suffix="₽" error={error} onBlur={() => touch(key)}/>
      <p className={`text-right text-[13px] ${error === "Больше доступного" ? "text-destructive" : "text-muted-foreground"}`}>Доступно <span className="tabular-nums">{money(available(who), "RUB")}</span></p>
    </div>;
  };
  return <CommandForm compact singleAction title="Расход" label="Записать" path="accounting" onClose={onClose}
    payload={() => moneyPayload("expense", v, current)} submitDisabled={!valid}
    onConflictRefresh={async () => {
      const fresh = await api<AccountingSnapshot>("accounting");
      cache.setQueryData(["next", "accounting"], fresh); setCurrent(fresh);
    }}>
    <PartyChoice compact label="Счёт" value={v.owner} options={owners} onChange={value => set(s => ({ ...s, owner: value as Owner | "both" }))} />
    <div ref={fieldsRef} className="space-y-4 expense-fields" onKeyDown={event => {
      if (event.key !== "Enter" || !(event.target instanceof HTMLInputElement)) return;
      event.preventDefault(); event.stopPropagation();
      const fields = Array.from(event.currentTarget.querySelectorAll<HTMLInputElement>("input:not([disabled])"));
      const next = fields[fields.indexOf(event.target) + 1];
      if (next) next.focus(); else event.target.blur();
    }}>
      {v.owner === "both" ? <>
        {amountField("kolya", "kolya", "Коля")}
        {amountField("vlad", "vlad", "Влад")}
        <div className="flex items-center justify-between gap-3" data-expense-total><span className="text-sm text-muted-foreground">Итого</span><span className="text-base font-semibold tabular-nums">{money(total, "RUB")}</span></div>
      </> : amountField("amount", v.owner, "Сумма")}
      <Field label="За что" error={touched.description && !v.description.trim() ? "Укажите причину" : undefined}><Input value={v.description} maxLength={500} onChange={event => update("description", event.target.value)} onBlur={() => touch("description")}/></Field>
    </div>
  </CommandForm>;
}
